/**
 * Contratti della libreria schede: mix, creazione e filtri.
 *
 * `validaMix` resta in carico.ts (quote e somma). Qui stanno gli schemi Zod.
 * Una scheda vale per chiunque la svolga: nel mix entra qualunque attributo.
 */

import { z } from 'zod';
import { idSchema, normalizzaNome } from './contratti.js';

export const TIPI_SCHEDA = ['esercizio', 'partita'] as const;
export type TipoScheda = (typeof TIPI_SCHEDA)[number];

export const ETICHETTA_TIPO_SCHEDA: Record<TipoScheda, string> = {
  esercizio: 'Esercizio',
  partita: 'Partita',
};

export const voceMixInSchema = z.object({
  attributoId: idSchema,
  quota: z
    .number({ invalid_type_error: 'La quota deve essere un intero tra 1 e 100.' })
    .int('La quota deve essere un intero tra 1 e 100.')
    .min(1, 'La quota deve essere un intero tra 1 e 100.')
    .max(100, 'La quota deve essere un intero tra 1 e 100.'),
});

export function rifiutaMix(mix: { attributoId: string; quota: number }[], ctx: z.RefinementCtx) {
  const ids = mix.map((v) => v.attributoId);
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['mix'],
      message: "L'attributo è ripetuto nel mix.",
    });
  }
  if (mix.reduce((s, v) => s + v.quota, 0) !== 100) {
    ctx.addIssue({ code: 'custom', path: ['mix'], message: 'Il mix deve sommare a 100.' });
  }
}

/** RPE di riferimento di una scheda o di un test. */
export const rpeRiferimentoSchema = z
  .number({ invalid_type_error: 'RPE deve essere un intero tra 1 e 10.' })
  .int()
  .min(1, 'RPE deve essere un intero tra 1 e 10.')
  .max(10, 'RPE deve essere un intero tra 1 e 10.');

/** Minuti di riferimento di una scheda o di un test. */
export const minutiRiferimentoSchema = z
  .number({ invalid_type_error: 'I minuti devono essere un intero maggiore di 0.' })
  .int()
  .min(1, 'I minuti devono essere un intero maggiore di 0.')
  .max(32767, 'I minuti superano il massimo.');

const campiScheda = {
  nome: z
    .string()
    .trim()
    .min(1, 'Il nome è obbligatorio.')
    .max(80, 'Massimo 80 caratteri.')
    .transform(normalizzaNome),
  rpeRiferimento: rpeRiferimentoSchema,
  minutiRiferimento: minutiRiferimentoSchema,
  tipo: z.enum(TIPI_SCHEDA, { message: 'Tipo non valido.' }).default('esercizio'),
  mix: z.array(voceMixInSchema).min(1, 'Il mix è vuoto.'),
};

export const creaSchedaSchema = z
  .object(campiScheda)
  .superRefine((d, ctx) => rifiutaMix(d.mix, ctx));

export type DatiScheda = z.infer<typeof creaSchedaSchema>;

export const modificaSchedaSchema = creaSchedaSchema;
export type DatiModificaScheda = z.infer<typeof modificaSchedaSchema>;

export const statoSchedaSchema = z.object({ attiva: z.boolean() });

const tipoQuery = z.union([z.enum(TIPI_SCHEDA), z.array(z.enum(TIPI_SCHEDA))]);

export const filtriSchedeSchema = z
  .object({
    tipo: tipoQuery.optional(),
    q: z.string().optional(),
    attiva: z.enum(['true', 'false']).optional(),
  })
  .transform((f) => ({
    ...(f.tipo && { tipo: Array.isArray(f.tipo) ? f.tipo : [f.tipo] }),
    ...(f.q?.trim() && { q: f.q.trim() }),
    ...(f.attiva !== undefined && { attiva: f.attiva === 'true' }),
  }));

export type FiltriSchede = z.output<typeof filtriSchedeSchema>;
